import { useQuery } from '@tanstack/react-query';
import { Col, Flex, Row } from 'components/base';
import {
  Button,
  matchWardsToLocationWard,
  Spacer,
  TextBox,
  ToggleButton,
  Visible,
  WardSelect,
} from 'design-system';
import { useRouter } from 'expo-router';
import { useUserSession } from 'hooks';
import { useIsSmallerThan } from 'hooks/useResponsive';
import { locationWardFetcher } from 'integration';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { strings } from 'strings';

type Screen = 'admissions' | 'patients';

interface HeaderProps {
  onSearchChange: (query?: string) => void;
  screen: Screen;
  numberOfAdmissions?: number;
  isSubmitting?: boolean;
  patientsAreSelected?: boolean;
  onSubmit?: ({ wardId }: { wardId: string }) => void;
}

export const Header: React.FC<HeaderProps> = ({
  onSearchChange,
  screen,
  numberOfAdmissions,
  isSubmitting,
  patientsAreSelected,
  onSubmit,
}) => {
  const router = useRouter();
  const userSession = useUserSession();

  const [search, setSearch] = useState<string>('');
  const [selectedWard, setSelectedWard] = useState<string>('');

  const { data: wardOptions = [] } = useQuery({
    queryKey: ['locationWards', userSession.ward],
    queryFn: () => locationWardFetcher(),
  });

  const wards = useMemo(() => {
    return matchWardsToLocationWard(userSession.wards, wardOptions);
  }, [userSession.wards, wardOptions]);

  const handleWardSelect = useCallback((wardId: string) => {
    setSelectedWard(wardId);
  }, []);

  const onChange = (value: string) => {
    setSearch(value);

    const trimmed = value.trim();

    if (!trimmed) {
      onSearchChange(undefined);
    } else {
      onSearchChange(trimmed);
    }
  };

  const handleChange = useCallback(
    (value: Screen) => {
      if (value === screen) {
        return;
      }

      value === 'patients'
        ? router.push({ pathname: `/patient/patients` })
        : router.push({ pathname: `/patient` });
    },
    [router, screen],
  );

  const flexDirection = useIsSmallerThan('sm') ? 'column' : 'row';
  const isSmall = useIsSmallerThan('md');
  const isMedium = useIsSmallerThan('lg');
  const canSubmit = patientsAreSelected || isSubmitting || selectedWard === '';

  const onAdmissions = screen === 'admissions';

  const buttonText = isSubmitting
    ? strings.DisplayLoading
    : `${strings.Move} (${numberOfAdmissions})`;

  const buttonSize = isMedium ? 'default' : 'large';

  const handleMove = () => onSubmit?.({ wardId: selectedWard });

  return (
    <Flex
      flexDirection={flexDirection}
      alignItems="center"
      justifyContent="space-between"
    >
      <Col flex={1}>
        <ToggleButton<Screen>
          value={screen}
          onChange={handleChange}
          options={[
            {
              value: 'admissions',
              display: strings.ToggleDisplayAdmissions,
            },
            {
              value: 'patients',
              display: strings.ToggleDisplayPatients,
            },
          ]}
        />
      </Col>

      <Col flex={onAdmissions ? 2 : 0.5} fullWidth>
        <Row flex={1}>
          <Visible if={onAdmissions && !isSmall}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <WardSelect
                isSmall={isMedium}
                wards={wards}
                selected={selectedWard}
                onSelect={handleWardSelect}
              />
              <Button
                variant="contained"
                text={buttonText}
                size={buttonSize}
                disabled={canSubmit}
                onPress={handleMove}
              />
              <Spacer width={'xs'} />
            </View>
          </Visible>
          <Col alignItems="center" flex={1}>
            <TextBox
              onChange={onChange}
              value={search}
              placeholder={strings.TextBoxPlaceholderSearch}
              autoResizeTextBox
            />
          </Col>
        </Row>
      </Col>
    </Flex>
  );
};
